import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
} from "react";
import { useAuth } from "./AuthContext";
import { GetUsageResponse, Plan, UserService } from "../services/UserService";

type UsageContextValue = {
  limits: GetUsageResponse | undefined;
  refresh: () => Promise<void>;
};

const UsageContext = createContext<UsageContextValue | undefined>(undefined);

export const UsageProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const defaultLimits = useMemo(() => {
    return {
      maxUsage: 0,
      used: 0,
      plan: Plan.Free,
      maxApiRequests: 0,
      usedApiRequests: 0,
      resetsAfter: 0
    };
  }, []);
  const [limits, setLimits] = useState<GetUsageResponse | undefined>(defaultLimits);

  const refresh = useCallback(async () => {
    if (!user) {
      setLimits(defaultLimits);
      return;
    }

    const response = await UserService.getUsage();

    if (!response.error) {
      setLimits(response.data);
    } else {
      setLimits(defaultLimits);
    }
  }, [user, defaultLimits]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <UsageContext.Provider value={{ limits, refresh }}>
      {children}
    </UsageContext.Provider>
  );
};

export const useUsage = (): UsageContextValue => {
  const ctx = useContext(UsageContext);
  if (!ctx) {
    throw new Error("useUsage must be used within UsageProvider");
  }
  return ctx;
};
